import { Divider, Stack, Typography } from "@mui/material";
import { ManifestReviewSummaryQuery } from "generated-graphql/graphql";

import { type ApplyFilter, GroupLabel } from "./ManifestReviewSummary";

type AssigneeCounts = ManifestReviewSummaryQuery["staffManifestReviewSummary"]["assignedCounts"];

// Cap the scrollable list at ~6 rows tall; a partial 7th row peeks to signal there's
// more to scroll. Each row is body2 text (~20px) + py:0.5 padding (8px) ≈ 28px.
const ROW_HEIGHT_PX = 28;
const MAX_VISIBLE_ROWS = 6.5;

/**
 * Per-reviewer assignment counts, rendered inside the Summary panel. The counts come
 * from the panel's query; clicking a reviewer filters the queue to their assignments.
 * Always rendered (with an empty state) so the panel layout is stable.
 */
export function AssignedByStaff({
  assignedCounts,
  onApplyFilter,
}: {
  assignedCounts: AssigneeCounts;
  onApplyFilter: ApplyFilter;
}) {
  return (
    <Stack sx={{ width: "100%" }}>
      <GroupLabel>Assignees</GroupLabel>
      {assignedCounts.length === 0 && (
        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic", py: 0.5 }}>
          No assignments yet
        </Typography>
      )}
      <Stack
        divider={<Divider flexItem />}
        sx={{ maxHeight: ROW_HEIGHT_PX * MAX_VISIBLE_ROWS, overflowY: "auto", pr: 0.5 }}
      >
        {assignedCounts.map(({ user, count }) => {
          const name = user.person
            ? `${user.person.first ?? ""} ${user.person.last ?? ""}`.trim()
            : "";
          return (
            <Stack
              key={user.id}
              direction="row"
              justifyContent="space-between"
              // Filter by email (readable in the omnisearch + resolved by the resolver),
              // not the user's uuid.
              onClick={() => onApplyFilter({ assignee: [user.email] })}
              sx={{ py: 0.5, cursor: "pointer", "&:hover": { color: "primary.main" } }}
            >
              <Typography variant="body2" color="inherit">
                {name || user.email}
              </Typography>
              <Typography variant="body2" color="inherit">
                {count}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
}
